import { useState, useEffect } from 'react';
import { InputField, SelectField, TextareaField } from '@/components/formParts';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { fetchInquiryUserTypeOptions, fetchInquiryOptions, MasterOption } from '@/data';
import { removeEmoji } from '@/utiils';

// バリデーション用のスキーマ設定
const schemaBase = z.object({
  last_name_kanji: z.string().min(1, { message: "姓（漢字）は必須です" })
    .max(50, { message: "姓（漢字）は50文字以内で入力してください" })
    .regex(/^[\u3005\u3006\u3400-\u4DBF\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uF900-\uFAFF　]+$/, { message: "ひらがな、カタカナ、漢字、全角スペース（1つまで）で入力してください" })
    .refine((val) => val.split('　').length <= 2, { message: "全角スペースは1つまでで入力してください" }),
  first_name_kanji: z.string().min(1, { message: "名（漢字）は必須です" })
    .max(50, { message: "名（漢字）は50文字以内で入力してください" })
    .regex(/^[\u3005\u3006\u3400-\u4DBF\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uF900-\uFAFF]+$/, { message: "ひらがな、カタカナ、漢字で入力してください" }),
  email: z.string().min(1, { message: "メールアドレスは必須です" })
    .email({ message: "メールアドレスの形式が正しくありません" }),
  purpose_detail: z.string().min(1, { message: "お問い合わせ内容は必須です" })
    .min(20, { message: "問い合わせ内容は20〜200文字で入力してください" })
    .max(200, { message: "問い合わせ内容は20〜200文字で入力してください" })
    .regex(/^(?!.*(?:<>|<[^>]*>|https?:|www\.|ftp:))[\s\S]*$/, { message: "URL、絵文字、HTMLタグは使用できません" }),
  job: z.string().min(1, { message: "職業形態は必須です" }),
  inquiry_type: z.string().min(1, { message: "問い合わせ種別は必須です" }),
  inquiry_user_type: z.string().min(1, { message: "利用者区分は必須です" }),
});

const schema = schemaBase;

export type ContactBasicFormData = z.infer<typeof schema>;

type Props = {
  enabled: boolean;
  onValidationChange: (data: { isValid: boolean; formData: ContactBasicFormData | undefined }) => void;
  shouldSubmit: boolean;
  /** ログイン時に自動入力する値（姓・名・メール） */
  preset?: { last_name_kanji: string; first_name_kanji: string; email: string };
  /** ログイン時は true にして姓・名・メールを編集不可にする */
  locked?: boolean;
};

// フォームコンポーネント
const ContactBasicForm = ({
  enabled,
  onValidationChange,
  shouldSubmit,
  preset,
  locked = false,
}: Props) => {
  const { register, watch, getValues, formState: { errors, isValid }, trigger, setValue } =
    useForm<ContactBasicFormData>({
      mode: 'onTouched',
      resolver: zodResolver(schema),
      defaultValues: {
        last_name_kanji: preset?.last_name_kanji ?? '',
        first_name_kanji: preset?.first_name_kanji ?? '',
        email: preset?.email ?? '',
        purpose_detail: '',
        job: '',
        inquiry_type: '',
        inquiry_user_type: '',
      }
    });

  const [inquiryOptions, setInquiryOptions] = useState<MasterOption[]>([]);
  const [inquiryUserOptions, setInquiryUserOptions] = useState<MasterOption[]>([]);

  // ログイン時プリセットをフォームに反映
  useEffect(() => {
    if (preset) {
      setValue('last_name_kanji', preset.last_name_kanji);
      setValue('first_name_kanji', preset.first_name_kanji);
      setValue('email', preset.email);
      // 値の変更を親へ即時反映
      (async () => { await trigger(['last_name_kanji','first_name_kanji','email']); })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset]);

  // マスター取得
  useEffect(() => {
    const loadData = async () => {
      try {
        const [inquiry_user, inquiry] = await Promise.all([
          fetchInquiryUserTypeOptions(),
          fetchInquiryOptions()
        ]);
        setInquiryUserOptions(inquiry_user);
        setInquiryOptions(inquiry);
      } catch (error) {
        console.error('データ取得エラー:', error);
      }
    };
    loadData();
  }, []);

  // 入力値を親に通知
  useEffect(() => {
    const subscription = watch(() => {
      onValidationChange({
        isValid,
        formData: getValues(),
      });
    });
    return () => subscription.unsubscribe();
  }, [watch, isValid, onValidationChange, getValues]);

  // 親からの submit 要求でバリデーション実行
  useEffect(() => {
    if (shouldSubmit) {
      trigger();
    }
  }, [shouldSubmit, trigger]);

  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <InputField
          label="姓（漢字）"
          type="text"
          register={register('last_name_kanji')}
          errors={errors}
          hasRequiredLabel={true}
          // disabled={locked}           // ← ログイン時は編集不可
          disabled={!enabled || locked}
        />
        <InputField
          label="名（漢字）"
          type="text"
          register={register('first_name_kanji')}
          errors={errors}
          hasRequiredLabel={true}
          //disabled={locked}           // ← ログイン時は編集不可
          disabled={!enabled || locked}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <InputField
          label="メールアドレス"
          type="email"
          register={register('email')}
          errors={errors}
          // disabled={locked}           // ← ログイン時は編集不可
          disabled={!enabled || locked}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SelectField
          label="利用者区分"
          options={inquiryUserOptions}
          placeholder="選択してください"
          register={register('inquiry_user_type')}
          errors={errors}
          hasRequiredLabel={true}
          // disabled={enabled} 
          disabled={!enabled }
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SelectField
          label="問い合わせ種別"
          options={inquiryOptions}
          placeholder="選択してください"
          register={register('inquiry_type')}
          errors={errors}
          hasRequiredLabel
          // disabled={enabled}
          disabled={!enabled}
        />
      </div>

      <TextareaField
        label="お問い合わせ内容"
        register={register('purpose_detail')}
        rows={3}
        errors={errors}
        hasRequiredLabel={true}
        // disabled={enabled}
        disabled={!enabled}
        onInput={(e) => removeEmoji(e.target as HTMLInputElement)}
      />
    </>
  );
};

export default ContactBasicForm;
